import { Table } from "@tanstack/react-table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Meta } from "@/@types/paginated";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useEffect, useState } from "react";
import { useFilterEngineAction } from "../duck/hook/action";
import { useFilterEngineHistoryState } from "../duck/hook/state";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  meta: Meta;
}

export function DataTablePagination<TData>({
  table,
  meta,
}: DataTablePaginationProps<TData>) {
  const { filters } = useFilterEngineHistoryState();
  const { updateFilter } = useFilterEngineAction();
  const [perPage, setPerPage] = useState(filters.meta?.perPage || 10);

  useEffect(() => {
    setPerPage(filters.meta.perPage);
    table.setPageSize(filters.meta.perPage);
  }, [filters.meta.perPage]);

  return (
    <div className="flex flex-col space-y-4 px-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
      <div className="flex-1 text-sm text-gray-600 text-center sm:text-left">
        {meta.total} registro(s) encontrado(s).
      </div>

      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-6">
        <div className="flex items-center justify-center space-x-2 sm:justify-start">
          <p className="text-sm font-medium">Linhas por página</p>
          <Select
            value={`${perPage}`}
            onValueChange={(value) => {
              const newPerPage = Number(value);
              setPerPage(newPerPage);
              updateFilter("meta", {
                ...filters.meta,
                perPage: newPerPage,
                page: 1,
              });
            }}
          >
            <SelectTrigger className="h-9 w-[80px] border-gray-300">
              <SelectValue placeholder={perPage} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-center space-x-2 sm:justify-start">
          <Button
            variant="outline"
            className="h-9 w-9 p-0"
            onClick={() => updateFilter("meta", { ...filters.meta, page: 1 })}
            disabled={!meta.prev}
            aria-label="Primeira página"
          >
            <ChevronsLeft />
          </Button>

          <Button
            variant="outline"
            className="h-9 w-9 p-0"
            onClick={() =>
              updateFilter("meta", {
                ...filters.meta,
                page: Math.max(1, filters.meta.page - 1),
              })
            }
            disabled={!meta.prev}
            aria-label="Página anterior"
          >
            <ChevronLeft />
          </Button>

          <span className="text-sm font-medium">
            Página {meta.currentPage} de {meta.lastPage}
          </span>

          <Button
            variant="outline"
            className="h-9 w-9 p-0"
            onClick={() =>
              updateFilter("meta", {
                ...filters.meta,
                page: filters.meta.page + 1,
              })
            }
            disabled={!meta.next}
            aria-label="Próxima página"
          >
            <ChevronRight />
          </Button>

          <Button
            variant="outline"
            className="h-9 w-9 p-0"
            onClick={() =>
              updateFilter("meta", { ...filters.meta, page: meta.lastPage })
            }
            disabled={!meta.next}
            aria-label="Última página"
          >
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
